import { debounce } from 'lodash';

export function init(element: ParentNode | null) {
  if (element) {
    new HorizontalScroller(element);
  }
}

const STRETCH_ITEMS_CLASS = 'stretchItems';

class HorizontalScrollerItem {
  scroller: HorizontalScroller;
  itemElement: HTMLElement;
  index: number;

  constructor(scroller: HorizontalScroller, itemElement: HTMLElement, index: number) {
    this.scroller = scroller;
    this.itemElement = itemElement;
    this.index = index;
  }
}

class HorizontalScroller {
  scrollerContainer: HTMLElement;
  itemsContainer: HTMLElement;
  items: HorizontalScrollerItem[];
  activeItem: HorizontalScrollerItem;
  leftButton: HTMLButtonElement;
  rightButton: HTMLButtonElement;
  numberButtonsContainer: HTMLElement | null;
  numberButtons: HTMLButtonElement[];
  itemsOnScreen: number;
  isLastWindowMobile: boolean;
  shouldStretchItems: boolean;

  constructor(element: ParentNode) {
    this.scrollerContainer = element as HTMLElement;
    this.itemsContainer = element.querySelector('.horizontal-scroller-items')
    this.items = Array.from(this.itemsContainer.querySelectorAll(':scope > section'))
      .map((itemElement: HTMLElement, index: number) => {
        return new HorizontalScrollerItem(this, itemElement, index);
      });

    this.activeItem = this.items[0];
    this.leftButton = element.querySelector(':scope > .horizontal-scroller-controls > button.horizontal-scroller-left');
    this.rightButton = element.querySelector(':scope > .horizontal-scroller-controls > button.horizontal-scroller-right');
    this.numberButtonsContainer = element.querySelector(':scope > .horizontal-scroller-number-buttons')
    this.itemsOnScreen = parseInt((element as HTMLElement).dataset['itemsOnScreen']) ?? 1;
    this.shouldStretchItems = (element as HTMLElement).dataset[STRETCH_ITEMS_CLASS] === 'true';

    if (this.items.length < 2) {
      this.activateOneItemMode();
      return;
    }

    this.initNumberButtons();

    this.toggleStretchingItems();

    this.leftButton.addEventListener('click', () => {
      this.scrollLeft();
    });

    this.rightButton.addEventListener('click', () => {
      this.scrollRight();
    });

    window.addEventListener('resize', debounce(() => {
      this.handleWindowResizeEvent();
    }, 300));

    this.scrollToIndex(0);
  }

  initNumberButtons() {
    if (this.numberButtonsContainer) {
      // We're going to completely recreate any existing number buttons
      this.numberButtonsContainer.innerHTML = '';
      this.numberButtons = [];

      const numberOfButtons = this.items.length - this.itemsOnScreen;

      for (let index = 0; index < this.items.length; index++) {
        const button = this.createNumberButton(index, numberOfButtons);

        this.numberButtons.push(button);
        this.numberButtonsContainer.appendChild(button);
      }
    }
  }

  createNumberButton(buttonIndex: number, maxInWideScreen: number) {
    const button = document.createElement('button');
    button.classList.add('horizontal-scroller-number-button');
    button.setAttribute('aria-controls', this.scrollerContainer.id);
    button.setAttribute('data-item-index', buttonIndex.toString());

    // We only need as many buttons as it takes to make sure all items can be scrolled
    //  onto the screen. The excess buttons will be hidden unless in mobile screen.
    if (buttonIndex > maxInWideScreen) {
      button.classList.add('mobile-number-button');
    }

    button.innerHTML = `
      <span class="sr-only">Scroll to item ${buttonIndex + 1}</span>
      <span aria-hidden="true">${buttonIndex + 1}</span>`.trim();

    button.addEventListener('click' ,() => {
      this.activeItem = this.items[buttonIndex]; // To catch number-button based movement.
      this.scrollToIndex(buttonIndex);
    });

    return button;
  }

  toggleStretchingItems() {
    this.items.forEach((item) => {
      // Stretching items only work if the item (section) contains only 1 child.
      //  If there is more than 1 child, we don't know which child's height
      //  should be stretched (And they're also displayed wrong in flex style: multiple columns)
      if (this.shouldStretchItems && item.itemElement.children.length == 1) {
        item.itemElement.classList.add(STRETCH_ITEMS_CLASS);
      } else {
        item.itemElement.classList.remove(STRETCH_ITEMS_CLASS);
      }
    });
  }

  updateAriaStuff() {
    this.items.forEach((item, index) => {
      if (item.index >= this.activeItem.index && item.index < this.activeItem.index + this.getItemsOnScreen()) {
        item.itemElement.removeAttribute('aria-hidden');
      } else {
        item.itemElement.setAttribute('aria-hidden', 'true');
      }
    });
  }

  indexAfter(index: number): number {
    return index + 1 <= (this.items.length - this.getItemsOnScreen()) ? index + 1 : 0;
  }

  indexBefore(index: number): number {
    return index === 0 ? this.items.length - this.getItemsOnScreen() : index - 1;
  }

  scrollLeft() {
    this.activeItem = this.items[this.indexBefore(this.activeItem.index)];
    this.scrollToIndex(this.activeItem.index);
  }

  scrollRight() {
    this.activeItem = this.items[this.indexAfter(this.activeItem.index)];
    this.scrollToIndex(this.activeItem.index);
  }

  scrollToIndex(index: number) {
    this.itemsContainer.style.left = `-${(100 / this.getItemsOnScreen()) * index}%`;
    this.numberButtons.forEach((button) => {
      button.classList.remove('active');
    });
    const possibleButton = this.numberButtons[index];
    if (possibleButton) {
      possibleButton.classList.add('active');
    }

    this.updateAriaStuff();
  }

  getItemsOnScreen() {
    if (this.isMobileDevice()) {
      return 1;
    }

    return this.itemsOnScreen;
  }

  isMobileDevice() {
    return window.innerWidth <= 767;
  }

  activateOneItemMode() {
    this.numberButtonsContainer?.classList?.add('hidden');
    this.leftButton?.classList?.add('one-item-mode');
    this.rightButton?.classList?.add('one-item-mode');
    this.itemsContainer.classList.add('one-item-mode');
    this.itemsContainer.closest('.horizontal-scroller-items-wrapper')?.classList?.add('one-item-mode');
  }

  handleWindowResizeEvent() {
    // If window is resized to mobile or vice versa, scroll to the first item.
    //  This'll help to prevent the display issue when changing window size between
    //  the small (mobile) and the large one (Because the number of item per screen are
    //  different between those.)
    if (this.isLastWindowMobile != this.isMobileDevice()) {
      this.activeItem = this.items[0];
      this.scrollToIndex(0);
    }

    this.isLastWindowMobile = this.isMobileDevice();
  }
}
